import { get } from 'lodash';
import { config } from 'api';
import { UNKNOWN_ERROR } from 'utils/errors';
import fetchAction from 'utils/fetchAction';
import addingPaymentMethodError from '../models/addingPaymentMethodError';

export const INIT_PAYMENTS_URL = `${config.apiBasePath}/payments/init-add-payment/account-settings`;
export const PAYPAL_STATUS_URL = `${config.apiBasePath}/payments/paypal/status`;
export const ADD_PAYMENT_METHOD_URL = `${config.apiBasePath}/payments/braintree`;
export const GET_ADD_PAYMENT_CONTEXT_URL = `${config.apiBasePath}/payments/add-payment-context/braintree`;
export const GET_VERIFICATION_CONTEXT_URL = `${config.apiBasePath}/payments/vbv-context`;
export const FINISH_VERIFICATION_URL = `${config.apiBasePath}/payments/finish-vbv/braintree`;

export const INIT_PAYMENTS_REQUEST = 'INIT_PAYMENTS_REQUEST';
export const INIT_PAYMENTS_SUCCESS = 'INIT_PAYMENTS_SUCCESS';
export const INIT_PAYMENTS_ERROR = 'INIT_PAYMENTS_ERROR';
export const INIT_PAYMENTS_REDIRECTING = 'INIT_PAYMENTS_REDIRECTING';
export const ADD_PAYMENT_METHOD_REQUEST = 'ADD_PAYMENT_METHOD_REQUEST';
export const ADD_PAYMENT_METHOD_SUCCESS = 'ADD_PAYMENT_METHOD_SUCCESS';
export const ADD_PAYMENT_METHOD_ERROR = 'ADD_PAYMENT_METHOD_ERROR';
export const ENABLE_PAYPAL = 'ENABLE_PAYPAL';
export const DISABLE_PAYPAL = 'DISABLE_PAYPAL';
export const RESET_PAYMENTS = 'RESET_PAYMENTS';
export const SHOW_CARD_VERIFICATION = 'SHOW_CARD_VERIFICATION';
export const HIDE_CARD_VERIFICATION = 'HIDE_CARD_VERIFICATION';
export const FINISH_VERIFICATION_REQUEST = 'FINISH_VERIFICATION_REQUEST';
export const FINISH_VERIFICATION_SUCCESS = 'FINISH_VERIFICATION_SUCCESS';
export const FINISH_VERIFICATION_ERROR = 'FINISH_VERIFICATION_ERROR';
export const GET_VERIFICATION_CONTEXT_REQUEST = 'GET_VERIFICATION_CONTEXT_REQUEST';
export const GET_VERIFICATION_CONTEXT_SUCCESS = 'GET_VERIFICATION_CONTEXT_SUCCESS';
export const GET_VERIFICATION_CONTEXT_ERROR = 'GET_VERIFICATION_CONTEXT_ERROR';
export const GET_ADD_PAYMENT_CONTEXT_REQUEST = 'GET_ADD_PAYMENT_CONTEXT_REQUEST';
export const GET_ADD_PAYMENT_CONTEXT_SUCCESS = 'GET_ADD_PAYMENT_CONTEXT_SUCCESS';
export const GET_ADD_PAYMENT_CONTEXT_ERROR = 'GET_ADD_PAYMENT_CONTEXT_ERROR';
export const PAYMENTS_SHOW_SPINNER = 'PAYMENTS_SHOW_SPINNER';
export const PAYMENTS_HIDE_SPINNER = 'PAYMENTS_HIDE_SPINNER';
export const SHOW_PAYMENTS_ERROR = 'SHOW_PAYMENTS_ERROR';
export const HIDE_PAYMENTS_ERROR = 'HIDE_PAYMENTS_ERROR';

export function initPaymentsRequest() {
  return {
    type: INIT_PAYMENTS_REQUEST,
  };
}

export function initPaymentsSuccess(token) {
  return {
    type: INIT_PAYMENTS_SUCCESS,
    token,
  };
}

export function initPaymentsError({ error } = {}) {
  return {
    type: INIT_PAYMENTS_ERROR,
    error,
  };
}

export function initPaymentsRedirecting() {
  return {
    type: INIT_PAYMENTS_REDIRECTING,
  };
}

export function initPayments() {
  return (dispatch, getState, { network }) => {
    const currentAddPaymentInitState = getState().payments.addPaymentInit;
    if (currentAddPaymentInitState.isInitialized || currentAddPaymentInitState.isInitPaymentsInProgress) {
      return Promise.resolve();
    }

    dispatch(initPaymentsRequest());
    return network.put(INIT_PAYMENTS_URL)
      .then((data) => {
        if (data.oldPaymentUrl) {
          dispatch(initPaymentsRedirecting());
          network.redirectTo({ to: `/webshop${data.oldPaymentUrl}` }, true);
        } else if (data.token) {
          dispatch(initPaymentsSuccess(data.token));
        } else {
          dispatch(initPaymentsError(UNKNOWN_ERROR));
        }
      })
      .catch(err => {
        dispatch(initPaymentsError(err.data || UNKNOWN_ERROR));
      });
  };
}

export function showCardVerification({ amount, exemptionRequested, billingInfo, bin, paymentMethodNonce } = {}) {
  return {
    type: SHOW_CARD_VERIFICATION,
    amount,
    exemptionRequested,
    billingInfo,
    bin,
    paymentMethodNonce,
  };
}

export function hideCardVerification() {
  return {
    type: HIDE_CARD_VERIFICATION,
  };
}

export function addPaymentMethodRequest() {
  return {
    type: ADD_PAYMENT_METHOD_REQUEST,
  };
}

export function addPaymentMethodSuccess() {
  return {
    type: ADD_PAYMENT_METHOD_SUCCESS,
  };
}

export function addPaymentMethodError(error) {
  return {
    type: ADD_PAYMENT_METHOD_ERROR,
    error,
  };
}

export function addPaymentMethod(payloadNonce, deviceData, onSuccess) {
  return (dispatch, getState, { network }) => {
    dispatch(addPaymentMethodRequest());
    return network.post(ADD_PAYMENT_METHOD_URL, {
      body: JSON.stringify({ payloadNonce, deviceData }),
    })
      .then((response) => {
        const { vbvRequired, ...paymentData } = response;

        if (vbvRequired) {
          dispatch(showCardVerification(paymentData));
          return;
        }
        dispatch(addPaymentMethodSuccess());
        if (onSuccess) {
          onSuccess();
        }
      })
      .catch((error) => {
        dispatch(addPaymentMethodError(
          addingPaymentMethodError[get(error, 'data.code')] || addingPaymentMethodError.UNKNOWN_ERROR
        ));
      });
  };
}

export function finishVerificationRequest() {
  return {
    type: FINISH_VERIFICATION_REQUEST,
  };
}

export function finishVerificationSuccess() {
  return {
    type: FINISH_VERIFICATION_SUCCESS,
  };
}

export function finishVerificationError(error) {
  return {
    type: FINISH_VERIFICATION_ERROR,
    error,
  };
}

export function finishVerification(paymentMethodNonce, deviceData, onSuccess) {
  return (dispatch, getState, { network }) => {
    dispatch(finishVerificationRequest());
    return network.post(FINISH_VERIFICATION_URL, {
      body: JSON.stringify({ paymentMethodNonce, deviceData }),
    })
      .then(() => {
        dispatch(finishVerificationSuccess());
        if (onSuccess) {
          onSuccess();
        }
      })
      .catch(error => {
        dispatch(finishVerificationError(
          addingPaymentMethodError[get(error, 'data.code')] || addingPaymentMethodError.UNKNOWN_ERROR
        ));
      });
  };
}

export const getVerificationContext = fetchAction({
  actions: [
    GET_VERIFICATION_CONTEXT_REQUEST,
    GET_VERIFICATION_CONTEXT_SUCCESS,
    GET_VERIFICATION_CONTEXT_ERROR,
  ],
  url: GET_VERIFICATION_CONTEXT_URL,
});

export const getAddPaymentContext = fetchAction({
  actions: [
    GET_ADD_PAYMENT_CONTEXT_REQUEST,
    GET_ADD_PAYMENT_CONTEXT_SUCCESS,
    GET_ADD_PAYMENT_CONTEXT_ERROR,
  ],
  url: GET_ADD_PAYMENT_CONTEXT_URL,
});

export function enablePayPal(payPalActionType) {
  return {
    type: ENABLE_PAYPAL,
    payPalActionType,
  };
}

export function disablePayPal() {
  return {
    type: DISABLE_PAYPAL,
  };
}

export function checkPayPalStatus() {
  return (dispatch, getState, { network }) =>
    network.get(PAYPAL_STATUS_URL)
      .then((data) => {
        if (data.payPalAccessible) {
          dispatch(enablePayPal(data.payPalActionType));
        } else {
          dispatch(disablePayPal());
        }
      })
      .catch(() => {
        dispatch(disablePayPal());
      });
}

export function showSpinner() {
  return {
    type: PAYMENTS_SHOW_SPINNER,
  };
}

export function hideSpinner() {
  return {
    type: PAYMENTS_HIDE_SPINNER,
  };
}

export function showPaymentsError(error) {
  return {
    type: SHOW_PAYMENTS_ERROR,
    error,
  };
}

export function hidePaymentsError() {
  return {
    type: HIDE_PAYMENTS_ERROR,
  };
}

export function resetPayments() {
  return {
    type: RESET_PAYMENTS,
  };
}
